// components/JobCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import JobDetails from "./jobDetails";
import { QUERY_MATCH_ENGINE_JOB_DRY_RUN } from "../../utils/graphql/jobs/queries.js";

const ICON_ALIASES = {
  woman: "👩",
  female: "♀️",
  man: "👨",
  male: "♂️",
  group: "👥",
  people: "👥",
  language: "🗣️",
  location: "📍",
  time: "🕒",
  schedule: "📅",
  required: "✅",
  priority: "🎯",
  balance: "⚖️",
  specialty: "🧩",
  home: "🏠",
  communication: "💬",
};

function resolveBadgeIcon(iconValue) {
  const value = (iconValue || "").trim();
  if (!value) return "";

  const aliasKey = value
    .toLowerCase()
    .replace(/^\[+|\]+$/g, "")
    .replace(/^:+|:+$/g, "")
    .replace(/\s+/g, "")
    .replace(/_/g, "");

  return ICON_ALIASES[aliasKey] || value;
}

function getJobHeaderColor(job) {

  if (!job.active) {
    return "bg-success text-white";
  }

  if (!job?.meetingSnapshot?.startDateTime) return "";

  const start = new Date(job.meetingSnapshot.startDateTime);
  const now = new Date();
  const diffMs = start - now; // milliseconds difference
  const diffHours = diffMs / (1000 * 60 * 60); // convert to hours

  if (diffHours <= 24 && diffHours >= 0) {
    return "bg-danger text-white";
  } else if (diffHours <= 36 && diffHours >= 0) {
    return "bg-warning text-dark";
  } else {
    return "bg-info";
  }
}

export default function JobCard({ job, onSelect, onMetricsToggle, allowMetrics = false }) {
  const isActive = job.active;
  const isClosed = job.active === false;
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  const headerClass = `card-header job-list-header ${getJobHeaderColor(job)}`;
  const requirementBadges = Array.isArray(job?.requirementBadges)
    ? job.requirementBadges
    : [];
  const legacyRequirements = Array.isArray(job?.requirements) ? job.requirements : [];
  const normalizedBadges = requirementBadges.length > 0
    ? requirementBadges
    : legacyRequirements.map((name) => ({ name, icon: null }));

  useEffect(() => {
    setShowScoreBreakdown(false);
  }, [job?._id]);

  useEffect(() => {
    onMetricsToggle?.(showScoreBreakdown);
  }, [onMetricsToggle, showScoreBreakdown]);

  const {
    data: dryRunData,
    loading: dryRunLoading,
    error: dryRunError,
  } = useQuery(QUERY_MATCH_ENGINE_JOB_DRY_RUN, {
    variables: { jobId: job?._id },
    skip: !isClosed || !showScoreBreakdown || !job?._id,
    fetchPolicy: "cache-and-network",
  });

  const scoringWeights = dryRunData?.matchEngineJobDryRun?.scoringWeights || null;
  const scoreRows = useMemo(
    () => dryRunData?.matchEngineJobDryRun?.applicants ?? [],
    [dryRunData]
  );
  const winnerUserId = useMemo(() => {
    const assignedUserId = job?.assignedTo?._id?.toString?.() || job?.assignedTo?._id || null;
    if (assignedUserId) return assignedUserId;
    return scoreRows[0]?.userId || null;
  }, [job?.assignedTo?._id, scoreRows]);

  const formatScore = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) return "—";
    return value.toFixed(3);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  };

  const formatContribution = (scoreValue, weightValue) => {
    const score = Number(scoreValue);
    const weight = Number(weightValue);
    if (!Number.isFinite(score) || !Number.isFinite(weight)) return "—";
    return (score * weight).toFixed(3);
  };

  const getWeightedContributionSum = (row) => {
    if (!scoringWeights) return null;

    const pairs = [
      [row.constraintScore, scoringWeights.constraint],
      [row.workloadScore, scoringWeights.meetingsHosted],
      [row.recentSubScore, scoringWeights.recentSubs],
      [row.totalSubScore, scoringWeights.totalSubs],
      [row.unassignedApplicationScore, scoringWeights.unassignedApplications],
      [row.applicationTimingScore, scoringWeights.applicationTiming],
    ];

    const total = pairs.reduce((sum, [scoreValue, weightValue]) => {
      const score = Number(scoreValue);
      const weight = Number(weightValue);
      if (!Number.isFinite(score) || !Number.isFinite(weight)) return sum;
      return sum + (score * weight);
    }, 0);

    return total.toFixed(3);
  };
  
  console.log("Rendering JobCard for job:", job._id, "Active:", isActive);
  const handleClick = (e) => {
    e.preventDefault();
    if ( onSelect ) onSelect(job);
  };

  const handleToggleScores = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowScoreBreakdown((current) => !current);
  };

  return (
    <div
      key={job._id}
      className={`card col-12 mb-3 job-card ${showScoreBreakdown ? "job-card-expanded" : ""}`}
    >
      <p className={headerClass}>{job.meetingSnapshot?.title || "Untitled"}</p>

      <div className="card-body text-center position-relative pb-5 job-card-body">
          <Link
            to="#"
            onClick={handleClick}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <JobDetails job={job} active={isActive} />
          </Link>

        <div className="mt-3 text-start">
          <h6 className="mb-2">Requirements</h6>
          {normalizedBadges.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {normalizedBadges.map((badge, idx) => (
                (() => {
                  const resolvedIcon = resolveBadgeIcon(badge.icon);
                  const hasIcon = Boolean(resolvedIcon);
                  return (
                <span
                  key={`${job._id}-requirement-${idx}`}
                  className="badge rounded-pill text-bg-light border"
                  title={badge.name}
                  style={{
                    fontSize: hasIcon ? "0.92rem" : "0.7rem",
                    lineHeight: 1,
                    padding: hasIcon ? "0.35rem 0.55rem" : "0.3rem 0.5rem",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {resolvedIcon ? resolvedIcon : badge.name}
                </span>
                  );
                })()
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">No requirements configured</p>
          )}
        </div>

        {isClosed && allowMetrics && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success rounded-circle position-absolute bottom-0 end-0 m-3 shadow-sm"
            onClick={handleToggleScores}
            aria-label={showScoreBreakdown ? "Hide applicant score breakdown" : "Show applicant score breakdown"}
            title={showScoreBreakdown ? "Hide applicant score breakdown" : "Show applicant score breakdown"}
            style={{ width: "2.5rem", height: "2.5rem", lineHeight: 1 }}
          >
            <i className={`bi ${showScoreBreakdown ? "bi-x-lg" : "bi-graph-up"}`}></i>
          </button>
        )}

        {isClosed && allowMetrics && showScoreBreakdown && (
          <div className="mt-4 text-start border-top pt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div>
                <h6 className="mb-1">Applicant Score Breakdown</h6>
                <small className="text-muted">Winner row highlighted for quick scan.</small>
              </div>
              {dryRunLoading && <small className="text-muted">Loading scores...</small>}
              {dryRunError && <small className="text-danger">Unable to load score breakdown.</small>}
            </div>

            {!dryRunLoading && !dryRunError && scoreRows.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-striped table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Applicant</th>
                      <th scope="col" className="text-end">Constraint Fit</th>
                      <th scope="col" className="text-end">Meetings Hosted</th>
                      <th scope="col" className="text-end">Recent Subs</th>
                      <th scope="col" className="text-end">Total Subs</th>
                      <th scope="col" className="text-end">Unassigned Apps</th>
                      <th scope="col" className="text-end">Applied At</th>
                      <th scope="col" className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreRows.map((row) => {
                      const isWinner = row.userId && winnerUserId && row.userId === winnerUserId;
                      return (
                        <tr key={row.applicationId || row.userId || row.userName} className={isWinner ? "job-score-winner-row" : ""}>
                          <td>
                            <div className="fw-semibold">{row.userName || "Unknown"}</div>
                            {row.appliedAt && (
                              <small className="text-muted">Applied {new Date(row.appliedAt).toLocaleDateString()}</small>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{`${row.matched ?? 0}/${row.total ?? 0}`}</div>
                            <small className="text-muted">Score {formatScore(row.constraintScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.constraintScore, scoringWeights?.constraint)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{row.meetingsHosted ?? 0}</div>
                            <small className="text-muted">Score {formatScore(row.workloadScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.workloadScore, scoringWeights?.meetingsHosted)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{row.recentSubJobs ?? 0}</div>
                            <small className="text-muted">Score {formatScore(row.recentSubScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.recentSubScore, scoringWeights?.recentSubs)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{row.totalSubJobs ?? 0}</div>
                            <small className="text-muted">Score {formatScore(row.totalSubScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.totalSubScore, scoringWeights?.totalSubs)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{row.unassignedApplicationJobs ?? 0}</div>
                            <small className="text-muted">Score {formatScore(row.unassignedApplicationScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.unassignedApplicationScore, scoringWeights?.unassignedApplications)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{formatDateTime(row.appliedAt)}</div>
                            <small className="text-muted">Score {formatScore(row.applicationTimingScore)}</small>
                            <small className="text-muted d-block">Weighted {formatContribution(row.applicationTimingScore, scoringWeights?.applicationTiming)}</small>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{formatScore(row.score)}</div>
                            <small className="text-muted d-block">Σ Weighted {getWeightedContributionSum(row) ?? "—"}</small>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!dryRunLoading && !dryRunError && scoreRows.length === 0 && (
              <p className="text-muted mb-0">No applicant score data available for this closed job.</p>
            )}
          </div>
        )}
        {/* {isActive ? (
          <Link
            to="#"
            onClick={handleClick}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <JobDetails job={job} active />
          </Link>
        ) : (
          <JobDetails job={job} />
        )} */}
      </div>
    </div>
  );
}